import {XIcon} from '@primer/octicons-react'
import type {MouseEvent} from 'react'

import type {ComposerImage} from '@github-ui/agent-sessions-interface/components/ChatComposer'
import styles from './ImageAttachmentToken.module.css'

/** Strips a file extension for a cleaner chip label (e.g. `image-1.png` -> `image-1`). */
function stripExtension(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot > 0 ? name.slice(0, dot) : name
}

export interface ImageAttachmentTokenProps {
  /** The image to preview. */
  image: ComposerImage
  /** Called when the user removes this image. Omit to render the token read-only. */
  onRemove?: (id: string) => void
  /** Called when the user activates the chip to open a larger preview. */
  onPreview: (image: ComposerImage, event: MouseEvent<HTMLButtonElement>) => void
}

/**
 * A preview chip for an attached image: a thumbnail, the (extension-stripped)
 * filename, and a remove button. Rendered inside the composer's attachment row.
 * Activating the chip opens a larger preview of the image.
 */
export function ImageAttachmentToken({image, onRemove, onPreview}: ImageAttachmentTokenProps) {
  return (
    <li className={styles.Chip}>
      <button
        type="button"
        className={styles.ChipPreview}
        onClick={event => onPreview(image, event)}
        aria-label={`Preview ${image.name}`}
        data-testid="chat-composer-image-preview"
      >
        <img
          className={styles.ChipThumb}
          src={image.dataUrl}
          alt=""
          aria-hidden="true"
          data-testid="chat-composer-image-thumb"
        />
        <span className={styles.ChipLabel}>{stripExtension(image.name)}</span>
      </button>
      {onRemove ? (
        <button
          type="button"
          className={styles.ChipRemove}
          aria-label={`Remove ${image.name}`}
          onClick={() => onRemove(image.id)}
          data-testid="chat-composer-image-remove"
        >
          <XIcon size={16} />
        </button>
      ) : null}
    </li>
  )
}
